/*
  media-gallery-v2 元素：变体媒体 gallery v2（Swiper 14 + variant metafield 数据源）。

  Swiper 取自 layout 的全局 bundle（swiper14-bundle.min.js，14.1.0，已预装 EffectFade /
  Navigation 等 8 个模块）。本文件曾单独 ESM 加载一份只含 core+EffectFade+Navigation 的
  swiper14-gallery.min.js，因为当时全局还是 9.4.1；全局升到 14 后那份副本已删除。

  与 SwiperMediaImages（sections.js）并存，互不引用。独立使用时 root 必须带
  class="swiper-media-images" —— 这是 VariantPicker._initSwiperMedias（theme.js）
  整块替换 / 按 variantId 缓存链路的抓取锚点；该链路只依赖 data-variant-id、
  内部 .swiper 元素上的 el.swiper 实例与还原时写入的 data-last-idx。
  theme-us 当前嵌在 pdp-media-gallery-v2 元素（assets/pdp-media-gallery-v2.js）内使用：
  锚点 class 在外层，本元素不带（避免双锚点），data-last-idx 由外层转发。

  slide 契约（theme-us 由 part-pdp-media-swiper-v2 渲染；其他主题为
  part-product-media-swiper-v2 / -v2-mobile）：
  - 每个 .swiper-slide 带 data-media-type="image|video|model"
  - 主图 img 带 js-mgv2-img（sale 角标 img 不带）
  - model slide 带 swiper-no-swiping（model-viewer 旋转手势与 swiper 拖拽互斥）
*/
class MediaGalleryV2 extends HTMLElement {
  #abort;
  #swiper;

  connectedCallback() {
    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl) throw new Error('media-gallery-v2: missing required child ".swiper"');

    // 缓存还原路径会把同一元素重新插回（theme.js 的 fetch 分支还会先 destroy 旧实例），一律重建
    if (this.#swiper && !this.#swiper.destroyed) {
      this.#swiper.destroy(true, false);
    }
    this.#swiper = new Swiper(swiperEl, {
      // observer 与 v9 版（sections.js SwiperMediaImages）对齐：CSS/图片晚于本模块就绪时
      // 容器初测为 0，网格（snapGrid）坏死会钳住 loop 跳转，靠 observer/ResizeObserver 自愈
      observer: true,
      observeParents: true,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      // fade 下 rewind 与 loop 视觉等价（fade 无物理位移），且避开 v14 无克隆 loop 的
      // DOM 旋转与 slideToLoop 下标映射——缩略图/dots 与 slide 下标保持 1:1
      rewind: true,
      slidesPerView: 1,
      touchStartPreventDefault: true,
      passiveListeners: false,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      on: {
        slideChange: (swiper) => this.#onSlideChange(swiper),
      },
    });

    if (this.dataset.lastIdx) {
      this.#swiper.slideTo(parseInt(this.dataset.lastIdx, 10), 0);
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('swiperControl:select', this.#onControlSelect, { signal });

    // fetch 整块替换（theme.js _initSwiperMedias）同步升级子树时，本回调先于子元素
    // （page-dots-swiper 等）的 connectedCallback 执行，此刻同步调用会撞上未初始化的
    // 内部状态；推迟到微任务，等同批子元素初始化完成再做首次同步
    const swiper = this.#swiper;
    queueMicrotask(() => {
      if (!swiper.destroyed) this.#onSlideChange(swiper);
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onControlSelect = (event) => {
    this.#swiper.slideTo(event.detail.index);
  };

  #onSlideChange(swiper) {
    if (swiper.slides.length === 0) return;
    const activeSlide = swiper.slides[swiper.activeIndex];

    this.querySelectorAll('.swiper-slide video').forEach((video) => {
      if (!activeSlide || !activeSlide.contains(video)) video.pause();
    });

    this.querySelector('page-dots-swiper')?.select(swiper.realIndex, false);

    const counter = this.querySelector('.js-mgv2-counter-current');
    if (counter) counter.textContent = swiper.realIndex + 1;
  }
}

if (!window.customElements.get('media-gallery-v2')) {
  window.customElements.define('media-gallery-v2', MediaGalleryV2);
}
